import { FC, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  ArrowBack as ArrowBackIcon,
  Cancel,
  Delete as DeleteIcon,
  Edit,
  Save,
} from "@mui/icons-material";
import DocumentTitle from "../../components/documentTitle/DocumentTitle";
import MainTitle from "../../components/mainTitle";
import ControlledTextField from "../../components/controlledComponents/ControlledTextField";
import { CONSTANTS } from "../../../utils/CONSTS";
import { logger, pages } from "../../../utils/common";
import {
  useObepByIdHook,
  useUpdateObepHook,
} from "../../../hooks/useObepHook";
import { ObepDetailViewProps } from "./obep.interfaces";

// Mirrors OpenAPI `CreateObepRecordRequest` / `UpdateObepRecordRequest` string constraints.
const NAME_MIN = 3;
const NAME_MAX = 100;
const DESCRIPTION_MIN = 10;
const DESCRIPTION_MAX = 500;
const MEASUREMENTS_MIN = 10;
const MEASUREMENTS_MAX = 500;
const OBJECTIVE_MIN = 3;
const OBJECTIVE_MAX = 100;

const obepEditSchema = z.object({
  name: z
    .string()
    .min(NAME_MIN, `Name must be at least ${NAME_MIN} characters`)
    .max(NAME_MAX, `Name must not exceed ${NAME_MAX} characters`),
  description: z
    .string()
    .min(DESCRIPTION_MIN, `Description must be at least ${DESCRIPTION_MIN} characters`)
    .max(
      DESCRIPTION_MAX,
      `Description must not exceed ${DESCRIPTION_MAX} characters`,
    ),
  measurements: z
    .string()
    .min(MEASUREMENTS_MIN, `Measurements must be at least ${MEASUREMENTS_MIN} characters`)
    .max(
      MEASUREMENTS_MAX,
      `Measurements must not exceed ${MEASUREMENTS_MAX} characters`,
    ),
  objectives: z
    .array(
      z.object({
        value: z
          .string()
          .refine(
            (s) => s.trim().length >= OBJECTIVE_MIN,
            `Objective must be at least ${OBJECTIVE_MIN} characters`,
          )
          .refine(
            (s) => s.trim().length <= OBJECTIVE_MAX,
            `Objective must not exceed ${OBJECTIVE_MAX} characters`,
          ),
      }),
    )
    .min(1, "At least one objective is required"),
});

type ObepEditFormData = z.infer<typeof obepEditSchema>;

const mapObjectivesToForm = (objectives: string[] = []) =>
  objectives.map((value) => ({ value }));

const MetaItem: FC<{ label: string; value?: string | number | null }> = ({
  label,
  value,
}) => (
  <Grid item xs={12} sm={6} md={4}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body1">
      {value === undefined || value === null || value === "" ? "—" : value}
    </Typography>
  </Grid>
);

const ObepDetailView: FC<ObepDetailViewProps> = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isEditMode, setIsEditMode] = useState(false);

  const { data: record, isLoading, error } = useObepByIdHook(id);
  const updateObepMutation = useUpdateObepHook();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ObepEditFormData>({
    resolver: zodResolver(obepEditSchema),
    defaultValues: {
      name: "",
      description: "",
      measurements: "",
      objectives: [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "objectives",
  });

  useEffect(() => {
    if (record && !isEditMode) {
      reset({
        name: record.obepName,
        measurements: record.measurements,
        description: record.description ?? "",
        objectives: mapObjectivesToForm(record.objectives),
      });
    }
  }, [record, isEditMode, reset]);

  const goBack = () => navigate(`${pages.academics.path}/obep`);

  const handleEdit = () => {
    if (!record) return;
    reset({
      name: record.obepName,
      measurements: record.measurements,
      description: record.description ?? "",
      objectives: mapObjectivesToForm(record.objectives),
    });
    setIsEditMode(true);
  };

  const handleCancel = () => {
    if (record) {
      reset({
        name: record.obepName,
        measurements: record.measurements,
        description: record.description ?? "",
        objectives: mapObjectivesToForm(record.objectives),
      });
    }
    setIsEditMode(false);
  };

  const onSubmit = async (data: ObepEditFormData) => {
    if (!id) return;

    const objectives = data.objectives.map((item) => item.value.trim());

    try {
      await updateObepMutation.mutateAsync({
        obepId: id,
        request: {
          name: data.name,
          description: data.description,
          measurements: data.measurements,
          objectives,
        },
      });
      logger.show_success("OBEP record updated successfully!");
      setIsEditMode(false);
    } catch (e) {
      logger.show_error(
        e instanceof Error
          ? e.message
          : "Failed to update OBEP record. Please try again.",
      );
    }
  };

  if (isLoading) {
    return (
      <>
        <DocumentTitle title={CONSTANTS.title.academics} />
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
          data-testid="obep-detail-loading"
        >
          <CircularProgress />
        </Box>
      </>
    );
  }

  if (error || !record) {
    return (
      <>
        <DocumentTitle title={CONSTANTS.title.academics} />
        <Box p={3}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={goBack}
            data-testid="obep-detail-back"
          >
            Back to OBEP Records
          </Button>
          <Alert severity="error" sx={{ mt: 2 }}>
            Failed to load OBEP record.
          </Alert>
        </Box>
      </>
    );
  }

  const objectives = record.objectives ?? [];
  const saving = isSubmitting || updateObepMutation.isPending;

  return (
    <>
      <DocumentTitle title={CONSTANTS.title.academics} />
      <Grid container spacing={3} data-testid="obep-detail-view">
        <Grid item xs={12}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={2}
          >
            <Box display="flex" alignItems="center" gap={1}>
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={goBack}
                data-testid="obep-detail-back"
              >
                Back
              </Button>
              <MainTitle title={record.obepName} />
            </Box>
            {!isEditMode && (
              <Button
                variant="contained"
                startIcon={<Edit />}
                onClick={handleEdit}
                data-testid="edit-obep-button"
              >
                Edit
              </Button>
            )}
          </Stack>
        </Grid>

        <Grid item xs={12}>
          <Card data-testid="obep-detail-meta">
            <CardContent>
              {updateObepMutation.isError && (
                <Alert severity="error" sx={{ mb: 3 }} data-testid="obep-edit-error">
                  {updateObepMutation.error?.message ||
                    "Failed to update OBEP record. Please try again."}
                </Alert>
              )}

              {isEditMode ? (
                <form
                  onSubmit={handleSubmit(onSubmit)}
                  data-testid="obep-edit-form"
                >
                  <Grid container spacing={3}>
                    <ControlledTextField
                      control={control}
                      name="name"
                      id="obep-edit-name"
                      label="Name"
                      required
                      errorMessage={errors.name?.message}
                      inputProps={{ "data-testid": "obep-edit-name-input" }}
                      data-testid="obep-edit-name-input"
                    />
                    <Grid item xs={9}>
                      <ControlledTextField
                        control={control}
                        name="measurements"
                        id="obep-edit-measurements"
                        label="Measurements"
                        required
                        errorMessage={errors.measurements?.message}
                        inputProps={{
                          "data-testid": "obep-edit-measurements-input",
                        }}
                        isGridItem={false}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <ControlledTextField
                        control={control}
                        name="description"
                        id="obep-edit-description"
                        label="Description"
                        required
                        multiline
                        rows={3}
                        errorMessage={errors.description?.message}
                        inputProps={{
                          "data-testid": "obep-edit-description-input",
                        }}
                        isGridItem={false}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{ mb: 1 }}
                      >
                        <Typography
                          variant="subtitle1"
                          component="h3"
                          data-testid="obep-edit-objectives-title"
                        >
                          Objectives
                        </Typography>
                        <Button
                          type="button"
                          variant="outlined"
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={() => append({ value: "" })}
                          data-testid="add-obep-edit-objective-button"
                        >
                          Add
                        </Button>
                      </Box>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                      >
                        Each objective must be {OBJECTIVE_MIN}–{OBJECTIVE_MAX}{" "}
                        characters. All objectives must be filled in to save.
                      </Typography>
                      {typeof errors.objectives?.message === "string" && (
                        <Typography
                          variant="body2"
                          color="error"
                          sx={{ mb: 2 }}
                          data-testid="obep-edit-objectives-error"
                        >
                          {errors.objectives.message}
                        </Typography>
                      )}
                      {fields.map((field, index) => (
                        <Box
                          key={field.id}
                          display="flex"
                          alignItems="flex-start"
                          gap={1}
                          sx={{ mb: 2 }}
                          data-testid={`obep-edit-objective-row-${index}`}
                        >
                          <Box flex={1}>
                            <ControlledTextField
                              control={control}
                              name={`objectives.${index}.value`}
                              id={`obep-edit-objective-${index}`}
                              label={`Objective ${index + 1}`}
                              required
                              multiline
                              rows={2}
                              errorMessage={
                                errors.objectives?.[index]?.value?.message
                              }
                              inputProps={{
                                "data-testid": `obep-edit-objectives-input-${index}`,
                              }}
                              isGridItem={false}
                              fullWidth
                            />
                          </Box>
                          <IconButton
                            type="button"
                            onClick={() => remove(index)}
                            color="error"
                            size="small"
                            aria-label={`Remove objective ${index + 1}`}
                            data-testid={`remove-obep-edit-objective-button-${index}`}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      ))}
                      {fields.length === 0 && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontStyle: "italic" }}
                          data-testid="obep-edit-objectives-empty-hint"
                        >
                          No objectives added. Click Add to create one.
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      <Divider sx={{ my: 2 }} />
                      <Box display="flex" gap={2} justifyContent="flex-end">
                        <Button
                          variant="outlined"
                          startIcon={<Cancel />}
                          onClick={handleCancel}
                          disabled={saving}
                          data-testid="cancel-edit-obep-button"
                        >
                          Cancel
                        </Button>
                        <Button
                          type="submit"
                          variant="contained"
                          startIcon={
                            saving ? <CircularProgress size={16} /> : <Save />
                          }
                          disabled={saving}
                          data-testid="save-obep-button"
                        >
                          {saving ? "Saving..." : "Save Changes"}
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </form>
              ) : (
                <>
                  <Grid container spacing={2}>
                    <MetaItem label="Measurements" value={record.measurements} />
                    <MetaItem label="Institute" value={record.institute?.name} />
                    <MetaItem label="Created By" value={record.createdBy?.name} />
                    <MetaItem
                      label="Created"
                      value={new Date(record.createdAt).toLocaleString()}
                    />
                  </Grid>

                  {record.description && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Typography variant="subtitle2" gutterBottom>
                        Description
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {record.description}
                      </Typography>
                    </>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {!isEditMode && (
          <Grid item xs={12}>
            <Card data-testid="obep-detail-objectives">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Objectives
                </Typography>
                {objectives.length === 0 ? (
                  <Alert severity="info" data-testid="obep-no-objectives">
                    No objectives are available for this record. The OBEP read
                    endpoint does not currently return objectives — they are
                    captured at creation time only.
                  </Alert>
                ) : (
                  <List disablePadding>
                    {objectives.map((objective, idx) => (
                      <Box key={idx} data-testid={`obep-objective-${idx}`}>
                        <ListItem disableGutters>
                          <ListItemText
                            primary={`${idx + 1}. ${objective}`}
                            primaryTypographyProps={{ variant: "body2" }}
                          />
                        </ListItem>
                        {idx < objectives.length - 1 && <Divider />}
                      </Box>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </>
  );
};

export default ObepDetailView;
